// ═══════════════════════════════════════════════════════════════
// OcMovimentacaoRisco.jsx — aba "Movimentação de risco"
//
// Responde a pergunta que o cliente fez por e-mail: quais companhias estao com
// PD alta, ou subiram muito entre duas leituras, SEM que a CVM as tenha
// reclassificado como em recuperacao, falencia ou liquidacao.
//
// ──────────────────────────────────────────────────────────────
// QUATRO RECUSAS. Nenhuma e estetica; cada uma ja custou caro neste produto.
//
// 1. NUNCA dizer "nao pediu RJ" quando o servidor disse que NAO SABE.
//    `eventos.disponivel === false` significa que a tabela de eventos nao pode
//    ser lida. Nesse caso a coluna some e entra um aviso. Mostrar a coluna
//    vazia leria como "nenhuma das 393 pediu recuperacao" — uma afirmacao falsa
//    sobre 393 companhias, dita com a autoridade de uma tela.
//
// 2. NUNCA chamar a variacao de mensal. O cliente escreveu "+10 p.p. MoM".
//    Nao e MoM: o RiskMind le balanco TRIMESTRAL, e a comparacao e entre duas
//    data-base. `dias_entre_bases` vai impresso no cabecalho justamente para
//    que ninguem precise supor.
//
// 3. NUNCA mostrar numerador sem denominador. "116 companhias" nao quer dizer
//    nada; "116 de 393 nesta data-base" quer.
//
// 4. NUNCA deixar a divergencia com o Screener ser descoberta pelo cliente.
//    Esta tela ancora numa data-base; o Screener le a ultima linha de cada CNPJ
//    e por isso mostra mais nomes. A diferenca aparece escrita, com o motivo.
//
// 5. NUNCA tratar a PD como continua. Ela e um ensemble e METADE DO PESO vem de
//    modelos que so emitem valores discretos (ver o bloco COMO A PD SE MOVE,
//    abaixo). Dai: a PD repete entre companhias, a variacao anda em degrau, e um
//    zero nao e estabilidade medida.
//
//    A TELA DIZ A CONSEQUENCIA, NAO O METODO. Decisao comercial de 18/09/2026,
//    depois de a primeira versao ter publicado os cinco pesos: os pesos sao
//    calibragem propria e nao vao para o cliente. O que vai e o efeito — que o
//    salto e travessia de faixa, que zero nao e estabilidade, que a lista empata.
//    Este bloco de comentario fica: ele e interno e e o que impede a proxima
//    pessoa de reintroduzir a afirmacao errada que a PR #10 publicou.
//
// 6. NUNCA mostrar companhia fora do pacote como se fosse do cliente, NEM
//    esconder que ela existe. Sao duas coisas diferentes e as duas importam.
//    O escopo e servido pelo servidor (services/oc/oc_carteira_escopo.js), nao
//    filtrado aqui: a aba "Todas as companhias" ja filtrava no navegador e o
//    servidor mandava as 553 pelo fio. Esta tela so RENDERIZA o que recebeu —
//    e se receber uma linha mascarada, ela sai marcada como tal, nunca com um
//    travessao que leria como dado faltando.
// ═══════════════════════════════════════════════════════════════

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ControleEscopo, SeletorTrimestres, temSeletorDeTrimestre, Direcao, ForaDoPacote } from './OcEscopoControles';

const C = {
  navy: '#0A1628', blue: '#1A3F6F', amber: '#C87B2A', gold: '#E8C56D', cream: '#FAF7F2',
  red: '#DC2626', green: '#15803D', amber2: '#D97706', agency: '#8067B7',
  muted: '#6b7280', line: '#e5e7eb', ink: '#0f172a', paper: '#ffffff',
};

const ratingColor = (r) => ['CC', 'C', 'D'].includes(r) ? C.red : r === 'CCC' ? C.amber
  : ['A', 'AA', 'AAA'].includes(r) ? C.green : C.blue;

const badge = {
  display: 'inline-block', padding: '2px 8px', borderRadius: 999,
  border: '1px solid', fontSize: 11.5, fontWeight: 700,
};

// pd_pct e variacao_pp ja vem do servidor em PONTOS PERCENTUAIS.
// Ver services/oc/oc_deterioracao.js — nao multiplicar por 100 aqui.
const fmtPct = (v) => v == null ? '—' : (+v).toFixed(2) + '%';
const fmtPp = (v) => v == null ? '—' : (v > 0 ? '+' : '') + (+v).toFixed(2) + ' p.p.';
const fmtData = (d) => {
  if (!d) return '—';
  const s = String(d).slice(0, 10).split('-');
  return s.length === 3 ? `${s[2]}/${s[1]}/${s[0]}` : String(d);
};

// ── COMO A PD SE MOVE ─────────────────────────────────────────────────────
// CORRIGIDO EM 18/09/2026. A versao anterior deste bloco afirmava que a PD
// saturava em 58,96% porque `computeBaselinePd` (services/engine/models/
// scorecard.js) e uma logistica com assintota em 60. Aquilo estava ERRADO:
// o scorecard e so UMA das cinco lentes, com peso 0,15, e nao existe teto em
// 60% — `pdAjust = Math.min(99, ...)` em services/engine/calc.js.
//
// A PD desta tela e `pd_final`, do ensemble em services/engine/calc.js:
//
//     Altman           0,30   emite APENAS 1,0% | 8,0% | 35,0%
//     Kanitz           0,20   emite APENAS 0,5% | 3% | 15% | 50%
//     Merton           0,20   continuo
//     Scorecard        0,15   continuo
//     Sector position  0,15   PD do setor x multiplicador
//     ml_bridge        0,00   inerte
//
// METADE DO PESO E DISCRETA. Altman e Kanitz respondem por 0,50 do ensemble e
// juntos emitem no maximo 12 combinacoes. Dai tres consequencias, todas
// MEDIDAS nesta data-base e nenhuma suposta:
//
//  (a) A PD REPETE. Das 84 companhias com PD >= 20% e sem ato publico, 17
//      cabem dentro de 0,28 p.p. umas das outras. Abaixo das primeiras
//      colocadas a coluna de PD nao ordena: ela empata.
//
//  (b) A VARIACAO ANDA EM DEGRAU. Cruzar a fronteira Zona Cinza -> Zona
//      Perigo do Altman move a PD em +8,10 p.p. sozinha (27 p.p. de pd_proxy
//      x 0,30). Kanitz Penumbra -> Insolvente move +7,00 p.p. Um "salto de
//      10 p.p." e quase sempre UMA DESSAS VIRADAS, nao uma piora continua.
//
//  (c) VARIACAO 0,00 NAO E ESTABILIDADE MEDIDA. Quer dizer que nenhuma lente
//      trocou de faixa e que as continuas mexeram pouco. O modelo nao afirmou
//      que a companhia esta parada — ele nao teve como dizer. Por isso zero
//      aqui nao sai verde: verde leria como melhora.
//
// Os degraus todos passam ainda por `pdAjust = pdPond * (1 + betaMult * 0,5)`,
// que e especifico da companhia. Por isso a tela NAO tenta inferir qual lente
// virou a partir do tamanho do degrau. Quem responde isso e a aba que le as
// lentes das duas data-base e compara as faixas.

const TIPO_EVENTO = {
  rj_re: 'recuperação',
  falencia: 'falência',
  liquidacao: 'liquidação',
  chapter_11: 'Chapter 11',
};

const inp = {
  padding: '7px 10px', border: `1px solid ${C.line}`, borderRadius: 8,
  fontSize: 13, color: C.ink, background: C.paper, outline: 'none',
};

const errBox = {
  background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
  borderRadius: 10, padding: '12px 14px', fontSize: 13, marginBottom: 12,
};

const avisoBox = {
  background: C.paper, border: `1px solid ${C.line}`, borderLeft: `3px solid ${C.amber}`,
  borderRadius: 10, padding: '10px 14px', fontSize: 12.5, color: C.ink,
  lineHeight: 1.55, marginBottom: 12,
};

// ── cabecalho: o que esta sendo comparado, e com que intervalo ──
function Cabecalho({ d }) {
  const dias = d.dias_entre_bases;
  return (
    <div style={{ ...avisoBox, borderLeftColor: C.navy }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>
        Posição de {fmtData(d.data_base)}
        {d.data_base_comparacao
          ? <> · comparada com {fmtData(d.data_base_comparacao)}</>
          : <> · sem base de comparação</>}
      </div>
      {d.data_base_comparacao ? (
        <div style={{ color: C.muted }}>
          {/* RECUSA 2: a variacao NAO e mensal, e a tela diz o intervalo real. */}
          A variação é entre estas duas data-base
          {dias != null && <> — <b>{dias} dias</b> de intervalo</>}, não entre meses.
          O RiskMind lê balanço trimestral: não existe leitura mensal.
          {d.motivo_comparacao && <> Base escolhida: {d.motivo_comparacao}.</>}
        </div>
      ) : (
        <div style={{ color: C.muted }}>
          Sem safra anterior que atenda ao piso de população, os cortes de nível continuam
          valendo; o salto entre bases fica indisponível. {d.motivo_comparacao}
        </div>
      )}
      <div style={{ color: C.muted, marginTop: 4 }}>
        {/* RECUSA 3: numerador sempre com denominador. */}
        <b>{d.universo?.n ?? '—'}</b> companhias nesta data-base
        {d.universo?.n_comparavel != null && <> · {d.universo.n_comparavel} com leitura anterior</>}
        {d.universo?.n_sem_base_anterior ? <> · {d.universo.n_sem_base_anterior} entraram agora</> : null}
      </div>
    </div>
  );
}

// ── RECUSA 1: sem a tabela de eventos, a coluna some e o aviso entra ──
function AvisoEventos({ eventos, universoN }) {
  if (eventos?.disponivel) {
    return (
      <div style={avisoBox}>
        <b>Sem registro público</b> quer dizer que a CVM não reclassificou a companhia como em
        recuperação judicial ou extrajudicial, falência ou liquidação até a data desta leitura.
        Não quer dizer ausência de aperto — quer dizer ausência de <i>ato público</i>.
        {eventos.n_no_universo != null && (
          /* RECUSA 3 tambem aqui: "34 das companhias" nao diz nada. */
          <> Nesta data-base, <b>{eventos.n_no_universo}</b> das {universoN} companhias
            do universo já têm registro público.</>
        )}
      </div>
    );
  }
  /* Duas ausencias diferentes, e o cliente merece saber qual das duas e:
     a tabela pode estar ilegivel (defeito) ou apenas ainda nao carregada
     (normal logo depois de um deploy, ate a primeira colheita rodar).

     `42P01` (relation does not exist) entra no segundo caso: a tabela e criada
     pela propria colheita, entao no primeiro deploy ela ainda nao existe. Foi
     exatamente o que a producao devolveu na estreia da rota. */
  const aindaNaoCarregou = /vazia|TABELA_VAZIA|42P01|does not exist/i.test(String(eventos?.motivo || ''));
  return (
    <div style={{ ...avisoBox, borderLeftColor: C.red }}>
      <b>
        {aindaNaoCarregou
          ? 'A lista de eventos de crédito ainda não foi carregada.'
          : 'A lista de eventos de crédito não pôde ser lida agora.'}
      </b>{' '}
      Por isso a coluna "registro público" está fora desta tela. Os cortes de PD continuam
      corretos — o que esta tela <i>não</i> pode afirmar, enquanto isso, é quem pediu e quem
      não pediu recuperação.
      {aindaNaoCarregou && <> A primeira carga roda na madrugada; depois dela a coluna volta sozinha.</>}
      {eventos?.motivo && <div style={{ color: C.muted, marginTop: 4, fontSize: 12 }}>Motivo: {eventos.motivo}</div>}
    </div>
  );
}

// ── RECUSA 4: a divergencia com o Screener aparece escrita ──
function Reconciliacao({ rec }) {
  const [aberto, setAberto] = useState(false);
  if (!rec || rec.indisponivel) return null;
  const fora = rec.fora_da_safra_ancora;
  if (!fora) return null;
  return (
    <div style={{ ...avisoBox, borderLeftColor: C.muted }}>
      <button
        onClick={() => setAberto(!aberto)}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: C.blue, fontSize: 12.5, fontWeight: 600 }}
      >
        {aberto ? '▾' : '▸'} Por que o Screener mostra {rec.screener_ultima_linha_por_cnpj} e esta tela mostra {rec.nesta_tela}?
      </button>
      {aberto && (
        <div style={{ marginTop: 6, color: C.muted }}>
          {rec.nota}
          <div style={{ marginTop: 6 }}>
            Na safra âncora: <b>{rec.na_safra_ancora}</b> · fora dela: <b>{fora}</b>.
          </div>
        </div>
      )}
    </div>
  );
}

const COLS = [
  { k: 'denom', label: 'empresa', al: 'left' },
  { k: 'setor', label: 'setor', al: 'left' },
  { k: 'rating', label: 'rating', al: 'center' },
  { k: 'pd_pct', label: 'PD nesta base', al: 'right' },
  { k: 'pd_anterior_pct', label: 'PD anterior', al: 'right' },
  { k: 'variacao_pp', label: 'variação', al: 'right' },
];

function Tabela({ linhas, onOpen, mostrarEvento }) {
  const cols = mostrarEvento
    ? COLS.concat([{ k: 'evento_publico', label: 'registro público', al: 'left' }])
    : COLS;
  if (!linhas.length) {
    return <div style={{ color: C.muted, fontSize: 13, padding: 20, textAlign: 'center' }}>nenhuma companhia neste corte.</div>;
  }
  return (
    <div style={{ background: C.paper, border: `1px solid ${C.line}`, borderRadius: 12, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f8fafc' }}>
            {cols.map((c) => (
              <th key={c.k} style={{ textAlign: c.al, padding: '9px 12px', color: C.muted, fontWeight: 600, fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: `1px solid ${C.line}`, whiteSpace: 'nowrap' }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {linhas.map((r) => {
          /* RECUSA 5. Zero e um fato: as duas leituras sao iguais. O que a tela
             recusa e a LEITURA de estabilidade que a cor verde daria. Zero sai
             neutro, com a explicacao do que zero significa neste ensemble. */
          const semMovimento = r.variacao_pp != null && +r.variacao_pp === 0;
          /* RECUSA 6. `fora_da_carteira` vem do servidor. A linha nao abre a
             ficha: `exigeNaCarteira` recusaria no servidor de qualquer jeito, e
             um clique que leva a um 403 e pior do que um clique que nao leva a
             lugar nenhum. */
          const fora = r.fora_da_carteira === true;
          const abrir = fora ? null : onOpen;
          return (
            <tr key={r.cnpj} onClick={() => abrir && abrir(r.cnpj)}
              title={fora ? 'Esta companhia não está no seu pacote. A ficha abre quando ela entra na sua carteira.' : undefined}
              style={{ cursor: abrir ? 'pointer' : 'default', borderBottom: '1px solid #f1f5f9', background: fora ? '#fcfcfd' : undefined }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = fora ? '#fcfcfd' : ''; }}>
              <td style={{ padding: '9px 12px' }}>
                <span style={{ fontWeight: 600 }}>{r.denom || '—'}</span>
                {r.ticker && <span style={{ color: C.muted, marginLeft: 6, fontSize: 11.5 }}>{r.ticker}</span>}
              </td>
              <td style={{ padding: '9px 12px', color: C.muted }}>{r.setor || '—'}</td>
              <td style={{ padding: '9px 12px', textAlign: 'center' }}>
                <span style={{ ...badge, color: ratingColor(r.rating), borderColor: ratingColor(r.rating) }}>{r.rating || '—'}</span>
                {r.rating_anterior && r.rating_anterior !== r.rating && (
                  <span style={{ color: C.muted, fontSize: 11, marginLeft: 6 }}>era {r.rating_anterior}</span>
                )}
              </td>
              <td style={{ padding: '9px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                {fora ? <ForaDoPacote /> : fmtPct(r.pd_pct)}
              </td>
              <td style={{ padding: '9px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: C.muted }}>
                {fora ? '' : fmtPct(r.pd_anterior_pct)}
              </td>
              <td style={{ padding: '9px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: semMovimento || r.variacao_pp == null ? C.muted : r.variacao_pp > 0 ? C.red : C.green }}>
                {fora ? <Direcao linha={r} /> : fmtPp(r.variacao_pp)}
                {!fora && semMovimento && (
                  <div
                    style={{ fontWeight: 400, fontSize: 10.5, color: C.muted, whiteSpace: 'nowrap' }}
                    title="As duas leituras são idênticas até a segunda casa. Isso quer dizer que nada mudou de faixa entre as duas data-base — não que a companhia esteja estável. O modelo não mediu estabilidade; ele não teve como distinguir."
                  >
                    leitura idêntica
                  </div>
                )}
              </td>
              {mostrarEvento && (
                <td style={{ padding: '9px 12px', fontSize: 12 }}>
                  {r.evento_publico ? (
                    <span style={{ color: C.red }}>
                      {TIPO_EVENTO[r.evento_publico.tipo] || r.evento_publico.tipo}
                      {' · '}{fmtData(r.evento_publico.data)}
                      {r.evento_publico.censurado && (
                        <span style={{ color: C.muted }} title="Já estava nessa situação antes da janela de coleta da CVM abrir; a data é a do primeiro documento, não a do pedido."> (anterior à janela)</span>
                      )}
                    </span>
                  ) : (
                    <span style={{ color: C.muted }}>sem registro</span>
                  )}
                </td>
              )}
            </tr>
          );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Bloco({ chave, dados, universoN, onOpen, eventosDisponiveis }) {
  // `sem_evento_publico` so existe quando ha como saber. null nao e lista vazia.
  const podeFiltrar = eventosDisponiveis && Array.isArray(dados.sem_evento_publico);
  const [somenteSem, setSomenteSem] = useState(podeFiltrar);
  const linhas = podeFiltrar && somenteSem ? dados.sem_evento_publico : dados.todos;
  const ehSalto = !chave.startsWith('pd_acima_');
  const titulo = ehSalto
    ? `Salto de ${dados.salto_minimo_pp} p.p. ou mais entre as duas data-base`
    : `PD igual ou acima de ${dados.corte_pd_pct}%`;
  /* RECUSA 5(b). O corte de salto nao mede piora continua: ele detecta, quase
     sempre, UMA virada de faixa numa das lentes discretas. Isso nao invalida o
     corte — cruzar a Zona Perigo do Altman e sinal de verdade — mas muda o que
     a lista significa, e o cliente tem que ler isso junto com ela. */
  const temSalto = dados.salto_minimo_pp != null;
  /* Ha linha mascarada nesta lista? Nao se pergunta ao modo, se pergunta ao
     DADO: o modo e intencao, a linha e fato, e e a linha que esta na tela. */
  const temMascarada = (linhas || []).some((l) => l.fora_da_carteira === true);

  return (
    <section style={{ marginBottom: 26 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 15, color: C.navy }}>{titulo}</h3>
        {/* RECUSA 3 de novo: aqui tambem o denominador anda junto. */}
        <span style={{ fontSize: 12.5, color: C.muted }}>
          {/* RECUSA 6 + RECUSA 3. No modo universo os dois numeros sao
              diferentes DE PROPOSITO, e dizer so o total faria o cliente ler
              como dele uma lista que em boa parte nao e. */}
          <b>{dados.n}</b> de {universoN} companhias
          {temMascarada && dados.n_na_carteira != null && (
            <> · <b>{dados.n_na_carteira}</b> {dados.n_na_carteira === 1 ? 'é do seu pacote' : 'são do seu pacote'}</>
          )}
          {podeFiltrar && <> · <b>{dados.sem_evento_publico.length}</b> sem registro público</>}
        </span>
        {podeFiltrar && (
          <label style={{ marginLeft: 'auto', fontSize: 12.5, color: C.ink, cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={somenteSem} onChange={(e) => setSomenteSem(e.target.checked)} style={{ marginRight: 6 }} />
            esconder quem já tem registro público
          </label>
        )}
      </div>
      {ehSalto && temSalto && (
        <div style={{ ...avisoBox, borderLeftColor: C.amber2, marginBottom: 10 }}>
          <b>Um salto aqui é quase sempre uma mudança de faixa, não uma piora contínua.</b>{' '}
          A PD não é uma régua lisa: ela muda em degrau quando a companhia troca de faixa numa
          das leituras estruturais. Um corte de {dados.salto_minimo_pp} p.p. captura, na prática,
          quem cruzou uma dessas fronteiras.
          <div style={{ marginTop: 6, color: C.muted }}>
            Isso não enfraquece a lista — cruzar uma fronteira é sinal de verdade, e é exatamente
            o que se quer ver. Mas quer dizer que o <i>tamanho</i> do salto indica qual fronteira
            foi cruzada, não a intensidade da piora. Duas companhias com +11 p.p. podem estar em
            situações diferentes.
          </div>
        </div>
      )}
      <Tabela linhas={linhas || []} onOpen={onOpen} mostrarEvento={eventosDisponiveis} />
    </section>
  );
}

export default function OcMovimentacaoRisco({ api, onOpen }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState('');
  /* Padrao carteira, do mesmo jeito que no servidor. Se a tela pedisse
     universo por padrao, a restricao passaria a depender de o cliente nao
     clicar — e o servidor estaria certo e a tela errada. */
  const [modo, setModo] = useState('carteira');
  /* `null` nos dois = deixa o servidor decidir. Guardar a data-base corrente
     como valor fixo assim que a tela abre congelaria a tela num trimestre: na
     virada para 30/09 ela continuaria pedindo 30/06 para sempre. */
  const [dataBase, setDataBase] = useState(null);
  const [comparacao, setComparacao] = useState(null);
  const [carregando, setCarregando] = useState(false);

  /* `d` NAO e zerado a cada troca: a tela ficaria branca entre o clique e a
     resposta, e o seletor sumiria junto (ele e montado a partir de `d`). Fica
     o painel anterior, esmaecido, com "consultando..." ao lado do controle. */
  useEffect(() => {
    let vivo = true;
    setErr(null); setCarregando(true);
    const qs = new URLSearchParams({ escopo: modo });
    if (dataBase) qs.set('data_base', dataBase);
    if (comparacao) qs.set('data_base_comparacao', comparacao);
    api('/api/oc/deterioracao?' + qs.toString())
      .then((r) => { if (vivo) setD(r); })
      .catch((e) => { if (vivo) setErr(e); })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [api, modo, dataBase, comparacao]);

  const filtrado = useMemo(() => {
    if (!d || !d.listas) return null;
    const termo = q.trim().toLowerCase();
    if (!termo) return d.listas;
    const casa = (l) => (l.denom || '').toLowerCase().includes(termo) || (l.ticker || '').toLowerCase().includes(termo);
    const out = {};
    for (const [k, v] of Object.entries(d.listas)) {
      out[k] = Object.assign({}, v, {
        todos: (v.todos || []).filter(casa),
        sem_evento_publico: Array.isArray(v.sem_evento_publico) ? v.sem_evento_publico.filter(casa) : v.sem_evento_publico,
        com_evento_publico: Array.isArray(v.com_evento_publico) ? v.com_evento_publico.filter(casa) : v.com_evento_publico,
      });
    }
    return out;
  }, [d, q]);

  if (err) {
    const gate = err.status === 401 || err.status === 403;
    return (
      <div style={errBox}>
        {gate
          ? 'Esta tela faz parte do Capital Aberto. Se o seu plano inclui e ela não abriu, fale com o suporte.'
          : `Não consegui carregar a movimentação de risco: ${err.message}`}
      </div>
    );
  }
  if (!d) return <div style={{ color: C.muted, fontSize: 13, padding: 20 }}>carregando movimentação de risco…</div>;
  if (d.erro) {
    return (
      <div style={errBox}>
        Sem leitura disponível para montar esta tela ({d.erro}). {d.motivo || ''}
      </div>
    );
  }

  const eventosDisponiveis = !!(d.eventos && d.eventos.disponivel);
  const ordem = Object.keys(d.listas || {}).sort((a, b) => {
    const pa = a.startsWith('pd_acima_') ? 0 : 1;
    const pb = b.startsWith('pd_acima_') ? 0 : 1;
    return pa - pb || a.localeCompare(b);
  });

  return (
    <div style={{ opacity: carregando ? 0.72 : 1, transition: 'opacity 120ms' }}>
      {/* Um dos dois, nunca os dois: o seletor absorveu este conteudo.
          Ver `temSeletorDeTrimestre` em OcEscopoControles.jsx. */}
      {!temSeletorDeTrimestre(d) && <Cabecalho d={d} />}
      <ControleEscopo esc={d.escopo} modo={modo} onModo={setModo} carregando={carregando} />
      <SeletorTrimestres d={d} dataBase={dataBase} onDataBase={setDataBase}
        comparacao={comparacao} onComparacao={setComparacao} carregando={carregando} />
      <AvisoEventos eventos={d.eventos} universoN={d.universo?.n ?? '—'} />
      <Reconciliacao rec={d.reconciliacao} />

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="buscar empresa ou ticker…" style={inp} />
      </div>

      {ordem.map((k) => (
        <Bloco
          key={k}
          chave={k}
          dados={(filtrado || d.listas)[k]}
          universoN={d.universo?.n ?? '—'}
          onOpen={onOpen}
          eventosDisponiveis={eventosDisponiveis}
        />
      ))}

      {d.aviso && (
        <div style={{ ...avisoBox, borderLeftColor: C.muted, marginTop: 6 }}>
          {d.aviso}
        </div>
      )}
    </div>
  );
}
